import {
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

import {
  SalesPagesContentRef,
  SalesPagesContentPublishedRef,
  SalesPagesRef,
  SalesPagesPublishedRef,
  db,
  storage,
} from "../../../../firebase";

/**
 * Data access for the Sales Pages CMS.
 *
 * Draft/publish model (see docs/SALES_PAGES_CMS.md):
 * - The admin only ever edits the DRAFT collections: `salesPages` and
 *   `salesPagesContent`.
 * - Publishing upserts a copy into the PUBLISHED collections
 *   (`salesPagesPublished` / `salesPagesContentPublished`) using the SAME
 *   document id. The live site reads the published collections; a staging view
 *   reads the drafts.
 * - A record is "publish pending" when its draft `updatedAt` is newer than its
 *   published twin's (or no published twin exists yet).
 */

// Normalize a Firestore Timestamp / Date / plain object to milliseconds.
const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts instanceof Date) return ts.getTime();
  if (ts.seconds != null) return ts.seconds * 1000;
  return 0;
};

// True when the draft is newer than its published twin (or not yet published).
export const isPublishPending = (draft, published) => {
  if (!draft) return false;
  if (!published) return true;
  return toMillis(draft.updatedAt) > toMillis(published.updatedAt);
};

// ---------------------------------------------------------------------------
// salesPages (one document per marketing page)
// ---------------------------------------------------------------------------

// Fetch all page documents sorted ascending by `order`. Includes disabled
// pages (admin needs to manage them).
export const getSalesPages = async () => {
  try {
    const snapshot = await getDocs(SalesPagesRef);
    const pages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    pages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return pages;
  } catch (error) {
    console.log("Error fetching sales pages:", error.message);
    return [];
  }
};

export const getSalesPageById = async (id) => {
  try {
    const snap = await getDoc(doc(db, "salesPages", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.log("Error fetching sales page:", error.message);
    return null;
  }
};

// Multiple pages may share a slug (e.g. several "home" versions). At most one
// page per slug may be enabled at a time, so enabling one disables the others
// that share its slug. This lets admins switch between versions without editing
// slugs. Adds the disabling updates to `batch`.
const disableOtherEnabledPagesWithSlug = async (batch, slug, exceptId) => {
  if (!slug) return;
  const snapshot = await getDocs(
    query(SalesPagesRef, where("slug", "==", slug))
  );
  snapshot.docs.forEach((d) => {
    const data = d.data();
    if (d.id !== exceptId && data.enabled) {
      batch.update(doc(db, "salesPages", d.id), { enabled: false });
    }
  });
};

export const createSalesPage = async (data, userId) => {
  const batch = writeBatch(db);
  const newRef = doc(SalesPagesRef);
  batch.set(newRef, {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
  if (data.enabled) {
    await disableOtherEnabledPagesWithSlug(batch, data.slug, newRef.id);
  }
  await batch.commit();
  return newRef;
};

// Updates a page. Sections link by `pageId`, so a slug rename only refreshes
// the denormalized `page` (slug) field on this page's sections — no FK cascade
// needed. Enforces the one-enabled-page-per-slug rule.
export const updateSalesPage = async (id, data, previousSlug, userId) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "salesPages", id), {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });

  if (previousSlug && data.slug && data.slug !== previousSlug) {
    const sectionsSnap = await getDocs(
      query(SalesPagesContentRef, where("pageId", "==", id))
    );
    sectionsSnap.docs.forEach((d) => {
      batch.update(doc(db, "salesPagesContent", d.id), { page: data.slug });
    });
  }

  if (data.enabled) {
    await disableOtherEnabledPagesWithSlug(batch, data.slug, id);
  }

  await batch.commit();
};

// Deletes a page and cascades: removes every `salesPagesContent` section owned
// by this page (matched by `pageId`). Deletes both draft and published twins
// immediately (a delete is not staged).
export const deleteSalesPage = async (id) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, "salesPages", id));
  batch.delete(doc(db, "salesPagesPublished", id));

  const sectionsSnap = await getDocs(
    query(SalesPagesContentRef, where("pageId", "==", id))
  );
  sectionsSnap.docs.forEach((d) => {
    batch.delete(doc(db, "salesPagesContent", d.id));
    batch.delete(doc(db, "salesPagesContentPublished", d.id));
  });
  await batch.commit();
};

// Menu buttons are simplified salesPages records that only add an external link
// to the nav (no slug, no sections). They carry `type: "menuButton"` + `href`.
// See docs/SALES_PAGES_CMS.md.
export const createMenuButton = async (data, userId) => {
  return await addDoc(SalesPagesRef, {
    type: "menuButton",
    title: data.title,
    href: data.href,
    order: data.order,
    enabled: data.enabled,
    showInNav: data.showInNav,
    openInNewTab: data.openInNewTab,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
};

export const updateMenuButton = async (id, data, userId) => {
  return await updateDoc(doc(db, "salesPages", id), {
    title: data.title,
    href: data.href,
    order: data.order,
    enabled: data.enabled,
    showInNav: data.showInNav,
    openInNewTab: data.openInNewTab,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
};

export const toggleSalesPageEnabled = async (page, enabled, userId) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "salesPages", page.id), {
    enabled,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
  if (enabled) {
    await disableOtherEnabledPagesWithSlug(batch, page.slug, page.id);
  }
  await batch.commit();
};

// ---------------------------------------------------------------------------
// salesPagesContent (one document per section on a page)
// Sections link to their owning page by `pageId` (the salesPages doc id). The
// slug (`page`) is denormalized onto each section for the public consumer's
// where('page','==',slug) query. Because slugs can be shared across pages,
// `pageId` is the source of truth for ownership.
// ---------------------------------------------------------------------------

// Fetch all section documents for a page (by pageId), sorted ascending by
// `order`. Includes disabled sections (admin needs to manage them). Sorting is
// done client-side to avoid requiring a composite index.
export const getSalesPageSections = async (pageId) => {
  try {
    const queryRef = query(
      SalesPagesContentRef,
      where("pageId", "==", pageId)
    );
    const snapshot = await getDocs(queryRef);
    const sections = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sections;
  } catch (error) {
    console.log("Error fetching sales page sections:", error.message);
    return [];
  }
};

export const getSalesSectionById = async (id) => {
  try {
    const docRef = doc(db, "salesPagesContent", id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.log("Error fetching sales section:", error.message);
    return null;
  }
};

export const createSalesSection = async (data, userId) => {
  return await addDoc(SalesPagesContentRef, {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
};

export const updateSalesSection = async (id, data, userId) => {
  const docRef = doc(db, "salesPagesContent", id);
  return await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
};

// Deletes a section: removes both the draft and its published twin immediately.
export const deleteSalesSection = async (id) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, "salesPagesContent", id));
  batch.delete(doc(db, "salesPagesContentPublished", id));
  await batch.commit();
};

export const toggleSectionEnabled = async (id, enabled, userId) => {
  const docRef = doc(db, "salesPagesContent", id);
  return await updateDoc(docRef, {
    enabled,
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
};

// ---------------------------------------------------------------------------
// Publishing (draft -> published twins, same document ids)
// ---------------------------------------------------------------------------

// Fetch published page twins as a map keyed by id (for publish-pending checks).
export const getPublishedSalesPagesMap = async () => {
  try {
    const snapshot = await getDocs(SalesPagesPublishedRef);
    const map = {};
    snapshot.docs.forEach((d) => {
      map[d.id] = { id: d.id, ...d.data() };
    });
    return map;
  } catch (error) {
    console.log("Error fetching published pages:", error.message);
    return {};
  }
};

export const getPublishedSalesPageById = async (id) => {
  try {
    const snap = await getDoc(doc(db, "salesPagesPublished", id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.log("Error fetching published page:", error.message);
    return null;
  }
};

// Fetch published section twins for a page (by pageId), keyed by id.
export const getPublishedSectionsMap = async (pageId) => {
  try {
    const snapshot = await getDocs(
      query(SalesPagesContentPublishedRef, where("pageId", "==", pageId))
    );
    const map = {};
    snapshot.docs.forEach((d) => {
      map[d.id] = { id: d.id, ...d.data() };
    });
    return map;
  } catch (error) {
    console.log("Error fetching published sections:", error.message);
    return {};
  }
};

export const getPublishedSectionById = async (id) => {
  try {
    const snap = await getDoc(doc(db, "salesPagesContentPublished", id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.log("Error fetching published section:", error.message);
    return null;
  }
};

// Strip the id and re-stamp publishedAt/By, keeping the draft's own updatedAt so
// the published twin's updatedAt reflects the draft that produced it.
const buildPublishedPayload = (draft, userId) => {
  const { id, ...rest } = draft;
  return {
    ...rest,
    publishedAt: serverTimestamp(),
    publishedBy: userId || null,
  };
};

// Publish a single section (upsert its published twin by same id) and bump the
// owning page's draft updatedAt so the page-level Publish button activates.
export const publishSection = async (sectionId, userId) => {
  const draft = await getSalesSectionById(sectionId);
  if (!draft) throw new Error("Draft section not found");
  const batch = writeBatch(db);
  batch.set(
    doc(db, "salesPagesContentPublished", sectionId),
    buildPublishedPayload(draft, userId)
  );
  // Touch the owning page draft so its publish-pending state reflects the change.
  if (draft.pageId) {
    batch.update(doc(db, "salesPages", draft.pageId), {
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    });
  }
  await batch.commit();
};

// Publish a page and all of its sections in one batch (upsert all twins by id).
export const publishPage = async (pageId, userId) => {
  const draftPage = await getSalesPageById(pageId);
  if (!draftPage) throw new Error("Draft page not found");
  const sections = await getSalesPageSections(pageId);

  const batch = writeBatch(db);
  batch.set(
    doc(db, "salesPagesPublished", pageId),
    buildPublishedPayload(draftPage, userId)
  );
  sections.forEach((section) => {
    batch.set(
      doc(db, "salesPagesContentPublished", section.id),
      buildPublishedPayload(section, userId)
    );
  });
  await batch.commit();
};

// Upload an image into the existing Storage bucket under a salesPagesContent/
// folder and return its public download URL.
export const uploadSalesImage = async (file, page = "home") => {
  const safeName = (file.name || "image").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `salesPagesContent/${page}/${uuidv4()}-${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// ---------------------------------------------------------------------------
// Uploads library (Storage `uploads/` folder)
// A shared bucket folder for arbitrary files (images or other) that can be
// referenced by URL from HTML sections. See QuickTales.com > Uploads.
// ---------------------------------------------------------------------------
const UPLOADS_FOLDER = "uploads";

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif|ico)$/i;

export const isImageUpload = (item) =>
  (item?.contentType || "").startsWith("image/") ||
  IMAGE_EXT_RE.test(item?.name || "");

// List all files in the uploads folder with their download URL + metadata.
export const listUploads = async () => {
  try {
    const folderRef = ref(storage, UPLOADS_FOLDER);
    const res = await listAll(folderRef);
    const items = await Promise.all(
      res.items.map(async (itemRef) => {
        const [url, meta] = await Promise.all([
          getDownloadURL(itemRef).catch(() => ""),
          getMetadata(itemRef).catch(() => ({})),
        ]);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          contentType: meta.contentType || "",
          size: meta.size || 0,
          timeCreated: meta.timeCreated || null,
        };
      })
    );
    // Newest first (fall back to name when timestamps are missing).
    items.sort((a, b) => {
      const ta = a.timeCreated ? new Date(a.timeCreated).getTime() : 0;
      const tb = b.timeCreated ? new Date(b.timeCreated).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return a.name.localeCompare(b.name);
    });
    return items;
  } catch (error) {
    console.log("Error listing uploads:", error.message);
    return [];
  }
};

// Upload a file into the uploads folder and return its public download URL.
// Stored as `uploads/<uuid>-<safeName>` to avoid collisions.
export const uploadPublicFile = async (file) => {
  const safeName = (file.name || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${UPLOADS_FOLDER}/${uuidv4()}-${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    // Preserve the original filename for display/reference.
    customMetadata: { originalName: file.name || safeName },
  });
  return await getDownloadURL(storageRef);
};

export const deleteUpload = async (fullPath) => {
  return await deleteObject(ref(storage, fullPath));
};
